import React, { useState, useEffect, useCallback } from 'react';

/**
 * Admin Users page - allows platform owner to search and impersonate any user
 */
export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  const loadUsers = useCallback(async (searchTerm = '', role = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (role) params.set('role', role);

      const response = await fetch(`/api/admin/users/search-all?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setError('');
      } else if (response.status === 403) {
        setError('Only platform owners can access this page');
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Error loading users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Debounced search when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers(search, filterRole);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, filterRole, loadUsers]);

  const handleImpersonate = async (user) => {
    if (!window.confirm(`Impersonate ${user.name}? This will log you in as them for 15 minutes.`)) {
      return;
    }

    setImpersonateLoading(true);
    try {
      const response = await fetch('/api/auth/impersonate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ targetUserId: user.id }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        window.location.href = '/dashboard';
      } else {
        const errData = await response.json();
        alert('Failed to impersonate: ' + (errData.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Impersonate error:', err);
      alert('Error: ' + err.message);
    } finally {
      setImpersonateLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'platform_owner': '#7c3aed',
      'owner': '#3b82f6',
      'manager': '#10b981',
      'adviser': '#f59e0b',
      'introducer': '#8b5cf6',
    };
    return colors[role] || '#6b7280';
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>Impersonate User</h1>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        Search for a user and impersonate their account (15 minute session)
      </p>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by name, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              minWidth: '150px',
            }}
          >
            <option value="">All Roles</option>
            <option value="platform_owner">Platform Owner</option>
            <option value="broker">Broker</option>
            <option value="introducer">Introducer</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            Loading users…
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
            {search || filterRole ? 'No users found matching your search' : 'No users found'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                  Name
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                  Email
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                  Role
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                  Company
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                    {user.name}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>
                    {user.email}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getRoleBadgeColor(user.role) + '20',
                        color: getRoleBadgeColor(user.role),
                        textTransform: 'capitalize',
                      }}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>
                    {user.company_name || '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button
                      onClick={() => handleImpersonate(user)}
                      disabled={impersonateLoading}
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        opacity: impersonateLoading ? 0.6 : 1,
                      }}
                    >
                      {impersonateLoading ? 'Loading…' : 'Impersonate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ marginTop: '24px', fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
        <strong>Note:</strong> Impersonation sessions last 15 minutes and are fully audited. Use this to debug issues or provide support to users.
      </p>
    </div>
  );
}
